const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const WARNING_BEFORE_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const POINTER_MOVE_THROTTLE_MS = 15 * 1000;
const STORAGE_KEY = 'd24-last-user-activity-at';

let initialized = false;
let supabaseClient = null;
let loggedOutCallback = null;
let lastActivityAt = Date.now();
let lastActivityWriteAt = 0;
let lastPointerMoveAt = 0;
let checkTimer = null;
let loggingOut = false;
let warningVisible = false;

function nowMs() {
  return Date.now();
}

function safeReadSharedActivity() {
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function safeWriteSharedActivity(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {}
}

function effectiveLastActivity() {
  const shared = safeReadSharedActivity();
  return Math.max(lastActivityAt, shared || 0);
}

export function markInactivityActivity() {
  if (loggingOut) return;

  const now = nowMs();
  if (now - lastActivityWriteAt < ACTIVITY_THROTTLE_MS) return;

  lastActivityAt = now;
  lastActivityWriteAt = now;
  safeWriteSharedActivity(now);

  if (warningVisible) {
    closeWarning();
  }
}

function onPointerMove() {
  const now = nowMs();
  if (now - lastPointerMoveAt < POINTER_MOVE_THROTTLE_MS) return;
  lastPointerMoveAt = now;
  markInactivityActivity();
}

function createWarning() {
  let backdrop = document.getElementById('d24InactivityWarning');
  if (backdrop) return backdrop;

  backdrop = document.createElement('div');
  backdrop.id = 'd24InactivityWarning';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-labelledby', 'd24InactivityWarningTitle');

  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '30000',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(17, 24, 39, .48)',
    backdropFilter: 'blur(3px)',
  });

  const card = document.createElement('div');
  Object.assign(card.style, {
    width: 'min(460px, 100%)',
    background: '#fff',
    border: '1px solid rgba(17, 24, 39, .14)',
    borderRadius: '14px',
    boxShadow: '0 24px 70px rgba(17, 24, 39, .22)',
    padding: '24px',
    color: '#20242a',
  });

  const title = document.createElement('h2');
  title.id = 'd24InactivityWarningTitle';
  title.textContent = 'Sesja wkrótce wygaśnie';
  Object.assign(title.style, {
    margin: '0 0 10px',
    fontSize: '20px',
    lineHeight: '1.25',
  });

  const text = document.createElement('p');
  text.textContent = 'Brak aktywności przez 55 minut. Za 5 minut nastąpi automatyczne wylogowanie.';
  Object.assign(text.style, {
    margin: '0 0 18px',
    color: '#5d6470',
    lineHeight: '1.5',
  });

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Zostań zalogowany';
  Object.assign(button.style, {
    width: '100%',
    minHeight: '42px',
    border: '0',
    borderRadius: '9px',
    padding: '10px 16px',
    background: '#17324d',
    color: '#fff',
    fontWeight: '700',
    cursor: 'pointer',
  });

  button.addEventListener('click', () => {
    markInactivityActivity();
    closeWarning();
  });

  card.append(title, text, button);
  backdrop.append(card);
  document.body.append(backdrop);
  return backdrop;
}

function showWarning() {
  if (warningVisible || loggingOut) return;
  const backdrop = createWarning();
  backdrop.style.display = 'flex';
  warningVisible = true;
}

function closeWarning() {
  const backdrop = document.getElementById('d24InactivityWarning');
  if (backdrop) backdrop.style.display = 'none';
  warningVisible = false;
}

async function hasActiveSession() {
  try {
    const { data, error } = await supabaseClient.auth.getSession();
    if (error) return false;
    return Boolean(data?.session?.access_token);
  } catch {
    return false;
  }
}

async function logoutForInactivity() {
  if (loggingOut || !supabaseClient) return;
  loggingOut = true;
  closeWarning();

  try {
    const { error } = await supabaseClient.auth.signOut({ scope: 'local' });
    if (error) throw error;
  } catch (error) {
    console.error('[D24 AUTH] Automatyczne wylogowanie:', error);
  }

  try {
    await loggedOutCallback?.();
  } catch (error) {
    console.error('[D24 AUTH] Obsługa automatycznego wylogowania:', error);
  } finally {
    loggingOut = false;
  }
}

async function checkIdleTime() {
  if (loggingOut || !supabaseClient) return;

  const sessionActive = await hasActiveSession();
  if (!sessionActive) {
    closeWarning();
    return;
  }

  const idleMs = Math.max(0, nowMs() - effectiveLastActivity());

  if (idleMs >= IDLE_TIMEOUT_MS) {
    await logoutForInactivity();
    return;
  }

  if (idleMs >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
    showWarning();
  } else {
    closeWarning();
  }
}

async function handleReturnToApp({ countAsActivity = false } = {}) {
  if (loggingOut || !supabaseClient) return;

  // Sprawdzamy stary znacznik CZASU ZANIM focus/kliknięcie zdąży go nadpisać.
  // To zabezpiecza przypadek, gdy Chrome uśpi kartę i setInterval nie wykona
  // się dokładnie po 60 minutach.
  const idleMs = Math.max(0, nowMs() - effectiveLastActivity());
  if (idleMs >= IDLE_TIMEOUT_MS) {
    await logoutForInactivity();
    return;
  }

  if (countAsActivity) markInactivityActivity();
  await checkIdleTime();
}

function bindActivityEvents() {
  const options = { passive: true };

  for (const eventName of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
    window.addEventListener(eventName, markInactivityActivity, options);
  }

  // Ruch myszy też liczymy jako aktywność, ale maksymalnie raz na 15 s,
  // żeby nie zapisywać localStorage setki razy na sekundę.
  window.addEventListener('pointermove', onPointerMove, options);

  window.addEventListener('focus', () => {
    handleReturnToApp({ countAsActivity:true }).catch(error => {
      console.warn('[D24 AUTH] Kontrola po powrocie do aplikacji:', error);
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleReturnToApp({ countAsActivity:false }).catch(error => {
        console.warn('[D24 AUTH] Kontrola po przywróceniu karty:', error);
      });
    }
  });

  window.addEventListener('storage', event => {
    if (event.key !== STORAGE_KEY) return;
    const value = Number(event.newValue);
    if (Number.isFinite(value) && value > lastActivityAt) {
      lastActivityAt = value;
      if (warningVisible) closeWarning();
    }
  });
}

export function initInactivityLogout({ supabase, onLoggedOut } = {}) {
  supabaseClient = supabase || supabaseClient;
  loggedOutCallback = typeof onLoggedOut === 'function'
    ? onLoggedOut
    : loggedOutCallback;

  if (initialized) {
    markInactivityActivity();
    return;
  }

  initialized = true;
  const shared = safeReadSharedActivity();
  lastActivityAt = shared || nowMs();
  safeWriteSharedActivity(lastActivityAt);

  bindActivityEvents();

  checkTimer = window.setInterval(() => {
    checkIdleTime().catch(error => {
      console.warn('[D24 AUTH] Kontrola bezczynności:', error);
    });
  }, CHECK_INTERVAL_MS);

  // Natychmiastowa kontrola jest ważna po przywróceniu karty/przeglądarki.
  checkIdleTime().catch(() => {});
}
